/*
 * Copyright 2026 Sony Corporation
 */

import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';

import { depthOfField, fieldOfView, type ResolvedOptics } from '../scene/camera-optics';
import type { Transform } from '../scene/types';
import type { Part, Point } from './subject';

/**
 * How well a rig sees a subject (camera-simulation.md §12).
 *
 * For every sample on the subject's surface and every camera: is it in frame,
 * facing the camera, in focus and not hidden behind another part of the body?
 * If so, how many millimetres of it land on one pixel? The best camera sets the
 * sample's detail and every qualifying camera counts toward its overlap.
 *
 * Pure arithmetic over the capsules the subject is built from; scenery is not
 * an occluder here (§12.5).
 */

export interface Sample {
  position: Point;
  normal: Point;
  /** Index into the parts the subject was built from. */
  part: number;
  /** Surface area the sample stands for, in square metres. */
  weight: number;
}

export interface CoverageCamera {
  id: string;
  position: Point;
  right: Point;
  up: Point;
  forward: Point;
  optics: ResolvedOptics;
}

export interface CoverageOptions {
  /** Detail the capture needs, e.g. 1 mm of subject per pixel. */
  targetMmPerPx: number;
  /** Cameras each point needs, e.g. two for stereo reconstruction. */
  minOverlap: number;
  /** Whether a point outside the depth of field counts as seen. */
  requireFocus: boolean;
  /** Steeper than this and a surface is a smear, not a texture. */
  maxObliquityDeg: number;
}

export const DEFAULT_COVERAGE_OPTIONS: CoverageOptions = {
  targetMmPerPx: 1,
  minOverlap: 2,
  requireFocus: true,
  maxObliquityDeg: 75,
};

export interface PartCoverage {
  name: string;
  /** Share of the part's surface no camera sees. */
  uncovered: number;
}

export interface CoverageStats {
  /** Shares of the visible surface, 0 to 1. */
  covered: number;
  meetsTarget: number;
  meetsOverlap: number;
  /** Area-weighted median detail of what is covered, in mm/px. */
  median: number | undefined;
  worst: { mmPerPx: number; part: string } | undefined;
  /** Every part, least covered first. */
  parts: PartCoverage[];
  cameras: number;
}

export interface CoverageResult {
  /** Best mm/px per sample; Infinity where no camera sees it, NaN where it is inside the body. */
  best: Float64Array;
  /** Qualifying cameras per sample. */
  count: Uint16Array;
  stats: CoverageStats;
}

type Vec = [number, number, number];

const sub = (a: Point, b: Point): Vec => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: Point, b: Point): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** The camera a rig is, in world space: Babylon composes a node's Euler rotation yaw, then pitch, then roll. */
export const coverageCamera = (id: string, transform: Transform, optics: ResolvedOptics): CoverageCamera => {
  const { rotation, position } = transform;
  const q = Quaternion.RotationYawPitchRoll(rotation.y, rotation.x, rotation.z);
  const axis = (x: number, y: number, z: number): Point => new Vector3(x, y, z).applyRotationQuaternion(q).asArray() as Vec;
  return {
    id,
    position: [position.x, position.y, position.z],
    right: axis(1, 0, 0),
    up: axis(0, 1, 0),
    forward: axis(0, 0, 1),
    optics,
  };
};

const sphereHit = (origin: Point, direction: Point, centre: Point, radius: number): number => {
  const oc = sub(origin, centre);
  const b = dot(oc, direction);
  const c = dot(oc, oc) - radius * radius;
  const h = b * b - c;
  return h < 0 ? -1 : -b - Math.sqrt(h);
};

/** Nearest hit of a unit ray on a capsule, or -1: the cylinder's side and its two end spheres. */
export const capsuleHit = (origin: Point, direction: Point, part: Part): number => {
  let nearest = Infinity;
  const consider = (t: number) => {
    if (t > 0 && t < nearest) {
      nearest = t;
    }
  };
  consider(sphereHit(origin, direction, part.a, part.radius));
  consider(sphereHit(origin, direction, part.b, part.radius));

  const ba = sub(part.b, part.a);
  const baba = dot(ba, ba);
  if (baba > 1e-12) {
    const oa = sub(origin, part.a);
    const bard = dot(ba, direction);
    const baoa = dot(ba, oa);
    const a = baba - bard * bard;
    const b = baba * dot(direction, oa) - baoa * bard;
    const c = baba * dot(oa, oa) - baoa * baoa - part.radius * part.radius * baba;
    const h = b * b - a * c;
    if (a > 1e-12 && h >= 0) {
      const t = (-b - Math.sqrt(h)) / a;
      const along = baoa + t * bard;
      if (along > 0 && along < baba) {
        consider(t);
      }
    }
  }
  return nearest === Infinity ? -1 : nearest;
};

const distanceToSegment = (point: Point, part: Part): number => {
  const ba = sub(part.b, part.a);
  const pa = sub(point, part.a);
  const baba = dot(ba, ba);
  const t = baba > 0 ? Math.min(Math.max(dot(pa, ba) / baba, 0), 1) : 0;
  const d = [pa[0] - ba[0] * t, pa[1] - ba[1] * t, pa[2] - ba[2] * t] as const;
  return Math.sqrt(dot(d, d));
};

/** Surface where two capsules overlap is inside the body: nobody can see it and nobody needs to. */
const buried = (sample: Sample, parts: readonly Part[]): boolean =>
  parts.some((part, index) => index !== sample.part && distanceToSegment(sample.position, part) < part.radius - 1e-4);

interface Lens {
  camera: CoverageCamera;
  tanHalfH: number;
  tanHalfV: number;
  near: number;
  far: number;
  /** mm/px per metre of depth, straight on. */
  pitchPerMetre: number;
}

const lensOf = (camera: CoverageCamera): Lens => {
  const fov = fieldOfView(camera.optics);
  const dof = depthOfField(camera.optics);
  const { sensor, optics, format } = camera.optics;
  return {
    camera,
    tanHalfH: Math.tan(fov.horizontal / 2),
    tanHalfV: Math.tan(fov.vertical / 2),
    near: dof.near,
    far: dof.far,
    pitchPerMetre: (1000 * sensor.width) / (optics.focal_length_mm * format.width_px),
  };
};

export const computeCoverage = (
  samples: readonly Sample[],
  parts: readonly Part[],
  cameras: readonly CoverageCamera[],
  options: CoverageOptions = DEFAULT_COVERAGE_OPTIONS
): CoverageResult => {
  const best = new Float64Array(samples.length);
  const count = new Uint16Array(samples.length);
  const lenses = cameras.map(lensOf);
  const minCos = Math.cos((options.maxObliquityDeg * Math.PI) / 180);
  // A hair off the surface, so a ray does not hit the part it starts on.
  const lift = 1e-3;

  samples.forEach((sample, index) => {
    if (buried(sample, parts)) {
      best[index] = NaN;
      return;
    }
    let finest = Infinity;
    let seen = 0;
    const origin: Vec = [
      sample.position[0] + sample.normal[0] * lift,
      sample.position[1] + sample.normal[1] * lift,
      sample.position[2] + sample.normal[2] * lift,
    ];

    for (const lens of lenses) {
      const { camera } = lens;
      const v = sub(sample.position, camera.position);
      const z = dot(v, camera.forward);
      if (z <= 0) {
        continue;
      }
      if (Math.abs(dot(v, camera.right)) > z * lens.tanHalfH || Math.abs(dot(v, camera.up)) > z * lens.tanHalfV) {
        continue;
      }
      const distance = Math.sqrt(dot(v, v));
      const cos = -dot(sample.normal, v) / distance;
      if (cos < minCos) {
        continue;
      }
      if (options.requireFocus && (z < lens.near || z > lens.far)) {
        continue;
      }

      const toCamera = sub(camera.position, origin);
      const reach = Math.sqrt(dot(toCamera, toCamera));
      const direction: Vec = [toCamera[0] / reach, toCamera[1] / reach, toCamera[2] / reach];
      if (
        parts.some((part) => {
          const t = capsuleHit(origin, direction, part);
          return t > 0 && t < reach;
        })
      ) {
        continue;
      }

      seen += 1;
      finest = Math.min(finest, (z * lens.pitchPerMetre) / cos);
    }

    best[index] = finest;
    count[index] = seen;
  });

  return { best, count, stats: summarize(samples, parts, best, count, options, cameras.length) };
};

const summarize = (
  samples: readonly Sample[],
  parts: readonly Part[],
  best: Float64Array,
  count: Uint16Array,
  options: CoverageOptions,
  cameras: number
): CoverageStats => {
  let total = 0;
  let covered = 0;
  let meetsTarget = 0;
  let meetsOverlap = 0;
  let worst: CoverageStats['worst'];
  const partTotal = new Array<number>(parts.length).fill(0);
  const partUncovered = new Array<number>(parts.length).fill(0);
  const seen: { mm: number; weight: number }[] = [];

  samples.forEach((sample, index) => {
    const mm = best[index];
    if (Number.isNaN(mm)) {
      return;
    }
    total += sample.weight;
    partTotal[sample.part] += sample.weight;
    if (mm === Infinity) {
      partUncovered[sample.part] += sample.weight;
      return;
    }
    covered += sample.weight;
    seen.push({ mm, weight: sample.weight });
    if (mm <= options.targetMmPerPx) {
      meetsTarget += sample.weight;
    }
    if (count[index] >= options.minOverlap) {
      meetsOverlap += sample.weight;
    }
    if (!worst || mm > worst.mmPerPx) {
      worst = { mmPerPx: mm, part: parts[sample.part].name };
    }
  });

  seen.sort((a, b) => a.mm - b.mm);
  let median: number | undefined;
  let running = 0;
  for (const entry of seen) {
    running += entry.weight;
    if (running >= covered / 2) {
      median = entry.mm;
      break;
    }
  }

  const share = (value: number) => (total > 0 ? value / total : 0);
  return {
    covered: share(covered),
    meetsTarget: share(meetsTarget),
    meetsOverlap: share(meetsOverlap),
    median,
    worst,
    parts: parts
      .map((part, index) => ({
        name: part.name,
        uncovered: partTotal[index] > 0 ? partUncovered[index] / partTotal[index] : 0,
      }))
      .sort((a, b) => b.uncovered - a.uncovered),
    cameras,
  };
};
